import React, { useCallback, useEffect, useRef, useState } from "react";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import {
  Box,
  Button,
  InputAdornment,
  LinearProgress,
  TextField,
  Typography,
  CircularProgress,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { postRequest } from "@/utils/request";
import { useSession, getSession } from "next-auth/react";
import { credentialSignIn } from "@/utils/credential-sign-in";
import { useCheckoutStore } from "@/components/context/checkout-store";
import { useAlertStore } from "@/components/context/alert-store";
import useLocalizedRouter from "@/hooks/use-localized-router";
import { decryptCredentials } from "@/utils/credentials-storage";
import Cookies from "js-cookie";
import {
  GUEST_CART_MERGE_KEY,
  GUEST_LOGIN_TRANSITION_COOKIE,
} from "@/components/core/session-monitor";

interface OtpFormProps {
  lang: string;
  handleChange: () => void;
}

const OTP_COUNTDOWN_SECONDS = 20;
const LS_CURRENT_EMAIL = "current-email";
const LS_LOGIN_CREDENTIALS = "login-credentials";
const LS_REGISTER_FORM_DATA = "register-formData";
const LS_OTP_EXPIRY = "otp-expiry";

function extractApiErrorMessage(error: any, fallback: string): string {
  if (Array.isArray(error) && error.length > 0) return error[0];
  return error?.error || error?.message || error?.err?.error || fallback;
}

function isTooManyAttempts(value: any): boolean {
  if (!value) return false;
  if (value.status === 429 || value?.response?.status === 429) return true;
  const str = value.error ?? value.message ?? (typeof value === "string" ? value : "");
  return str === "TOO_MANY_ATTEMPTS" || str === "429";
}

const OtpForm: React.FC<OtpFormProps> = ({ handleChange, lang }) => {
  const [otp, setOtp] = useState<string>("");
  const [isResendOtpLoaderVisible, setIsResendOtpLoaderVisible] = useState<boolean>(false);
  const [resendOTP, setResendOTP] = useState<boolean>(true);
  const [isProgressDisplayed, setIsProgressDisplayed] = useState<boolean>(true);
  const [progress, setProgress] = useState<number>(OTP_COUNTDOWN_SECONDS);
  const [email, setEmail] = useState<string | null>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const submitInFlightRef = useRef(false);
  const [currentOtpExpiry, setCurrentOtpExpiry] = useState<string | undefined>(undefined);
  const [minutesRemaining, setMinutesRemaining] = useState<number | null>(null);
  const queryClient = useQueryClient();
  const { setMessage } = useAlertStore();
  const router = useLocalizedRouter();
  const getAuthErrorMessage = (error?: string | null): string => {
    if (error === "TOO_MANY_ATTEMPTS" || error === "429") {
      return t`Too many attempts, please try again later`;
    }

    return error || t`An unexpected error occurred, please try again.`;
  };

  const showMessage = (text: string, severity: "warning" | "error" | "success") => {
    setMessage({ text, severity });
  };

  const showTooManyAttempts = (severity: "warning" | "error" = "error") => {
    showMessage(t`Too many attempts, please try again later`, severity);
  };

  const isOtpEmpty = () => !otp || otp.trim() === "";

  const getSavedLoginCredentials = () => localStorage.getItem(LS_LOGIN_CREDENTIALS);
  const getSavedRegisterFormData = () => localStorage.getItem(LS_REGISTER_FORM_DATA);
  const getSavedCurrentEmail = () => localStorage.getItem(LS_CURRENT_EMAIL);
  const getSavedOtpExpiry = () => localStorage.getItem(LS_OTP_EXPIRY);

  const clearOtpExpiry = () => localStorage.removeItem(LS_OTP_EXPIRY);
  const clearLoginCredentials = () => localStorage.removeItem(LS_LOGIN_CREDENTIALS);
  const clearRegisterFormData = () => localStorage.removeItem(LS_REGISTER_FORM_DATA);

  const setCurrentEmailInStorage = (value: string | null) => {
    localStorage.setItem(LS_CURRENT_EMAIL, value || "");
  };

  const parseRegisterFormData = (savedFormData: string) => JSON.parse(savedFormData);

  const requireOtp = (): boolean => {
    if (!isOtpEmpty()) {
      return true;
    }
    showMessage(t`Code is required`, "warning");
    return false;
  };

  const showUnexpectedWarning = () => {
    showMessage(t`An unexpected error occurred, please try again.`, "warning");
  };

  const handleResendError = (error: any) => {
    setIsResendOtpLoaderVisible(false);
    if (isTooManyAttempts(error)) {
      showTooManyAttempts("error");
      return;
    }
    showMessage(extractApiErrorMessage(error, t`Failed to resend OTP. Please try again.`), "error");
  };

  const resendOtpMutation = useMutation({
    mutationFn: (data: FormData) =>
      postRequest({ path: { name: "resendOTP" }, formData: data, method: "POST", lang }),
    onSuccess: (data: any) => {
      if (data.success === 0) {
        const apiError = extractApiErrorMessage(data.error, "");
        if (apiError) {
          showMessage(apiError, "error");
        } else if (data.tooManyAttempts || isTooManyAttempts(data)) {
          showTooManyAttempts("error");
        } else {
          showMessage(t`An unexpected error occurred. Please try again later.`, "error");
        }
        setIsResendOtpLoaderVisible(false);
        return;
      }

      setCurrentEmailInStorage(email);
      setResendOTP(false);
      if (data?.otp_expiry) {
        setCurrentOtpExpiry(data.otp_expiry);
      }
      showMessage(t`We have sent a new 4 digit OTP on your Email Address.`, "success");
      setIsResendOtpLoaderVisible(false);
    },
    onError: handleResendError,
  });

  const resendLoginOtpMutation = useMutation({
    mutationFn: (data: { email: string; password: string }) =>
      postRequest({
        path: { name: "resendLoginOTP" },
        body: { email: data.email, password: data.password },
        method: "POST",
        lang,
      }),
    onSuccess: (data: any) => {
      setIsResendOtpLoaderVisible(false);

      if (data?.success === 0) {
        const apiError = extractApiErrorMessage(data.error, "");
        if (apiError) {
          showMessage(apiError, "error");
        } else if (data.tooManyAttempts || isTooManyAttempts(data)) {
          showTooManyAttempts("error");
        } else {
          showMessage(t`Failed to resend OTP. Please try again.`, "error");
        }
        return;
      }

      setCurrentEmailInStorage(email);
      if (data?.otp_expiry) {
        setCurrentOtpExpiry(data.otp_expiry);
        localStorage.setItem(LS_OTP_EXPIRY, data.otp_expiry);
      }
      setResendOTP(true);
      showMessage(t`We have sent a new 6 digit OTP on your Email Address.`, "success");
    },
    onError: handleResendError,
  });

  const { data: session, update } = useSession();
  const [isGuest, setIsGuest] = useState<boolean>(false);
  useEffect(() => {
    const is_guest = session?.user.is_guest || "";
    setIsGuest(is_guest === "1");
  }, [session]);

  const { checkoutOtp, setActiveStep, setCheckoutOtp } = useCheckoutStore();
  const [isLoginFlow, setIsLoginFlow] = useState<boolean>(false);

  const updateMutation = useMutation({
    mutationFn: (data: object) =>
      postRequest({
        path: { name: "account" },
        body: { ...data },
        method: "PUT",
        lang,
      }),
  });

  const startSubmit = () => {
    if (submitInFlightRef.current) {
      return false;
    }
    submitInFlightRef.current = true;
    setIsLoading(true);
    return true;
  };

  const stopSubmit = () => {
    submitInFlightRef.current = false;
    setIsLoading(false);
  };

  const handleLoginSuccess = async () => {
    clearLoginCredentials();
    clearOtpExpiry();

    if (checkoutOtp) {
      queryClient.invalidateQueries({ queryKey: ["payment-addresses"] });
      setCheckoutOtp(false);
      const updatedSession = await getSession();
      if (!updatedSession?.user?.requiresTwoFactor) {
        setActiveStep(2);
      }
      return;
    }

    router.push("/account");
  };

  const handleLogin = async () => {
    if (submitInFlightRef.current) {
      return;
    }

    if (!requireOtp()) {
      return;
    }

    if (!startSubmit()) {
      return;
    }
    const savedLoginCredentials = getSavedLoginCredentials();

    if (!savedLoginCredentials) {
      showUnexpectedWarning();
      stopSubmit();
      return;
    }

    let credentials;
    try {
      credentials = await decryptCredentials(savedLoginCredentials);
    } catch {
      showUnexpectedWarning();
      stopSubmit();
      return;
    }

    try {
      const shouldMergeGuestCart =
        checkoutOtp && localStorage.getItem(GUEST_CART_MERGE_KEY) === "1";
      const result = await credentialSignIn({
        email: credentials.email,
        password: credentials.password,
        otp,
        action: "login",
        lang,
        ...(shouldMergeGuestCart ? { is_guest: "1", guest_transition: "1" } : {}),
      });

      queryClient.invalidateQueries({ queryKey: ["payment-addresses"] });
      queryClient.invalidateQueries({ queryKey: ["cart"] });

      if (!result || !result.ok) {
        showMessage(getAuthErrorMessage(result?.error), "warning");
      } else if (isTooManyAttempts(result)) {
        showTooManyAttempts("error");
      } else if (result?.error) {
        showMessage(getAuthErrorMessage(result.error), "warning");
      } else {
        if (shouldMergeGuestCart) {
          localStorage.removeItem(GUEST_CART_MERGE_KEY);
          Cookies.remove(GUEST_LOGIN_TRANSITION_COOKIE, { path: "/" });
        }
        await handleLoginSuccess();
      }
    } catch (error) {
      console.error("Error occurred during login", error);
      if (isTooManyAttempts(error)) {
        showTooManyAttempts("warning");
      } else {
        showMessage(t`An error occurred during login. Please try again.`, "warning");
      }
    } finally {
      stopSubmit();
    }
  };

  const handleLoggedInGuestRegistration = async (formData: any) => {
    const { email, password, phone: telephone, countryCode, checkboxState } = formData;

    const data = await updateMutation.mutateAsync({
      email,
      password,
      telephone: `+${countryCode} ${telephone}`,
      customer_group_id: 1, //register business account group ID - 10 for test, 11 for live
      agree: checkboxState.isPrivacyChecked,
      newsletter: checkboxState.isPrivacyChecked,
      offerz_plus_terms: checkboxState.isTermsChecked,
      email_otp: otp,
      is_guest: 0,
      force_otp: 1,
    });

    if (data.success === 0) {
      if (data.tooManyAttempts || isTooManyAttempts(data)) {
        showTooManyAttempts("error");
        return;
      }
      if (data.error.otp) {
        showMessage(data.error.otp, "warning");
      } else {
        showMessage(t`An unexpected error occurred. Please try again later.`, "error");
      }
      return;
    }

    clearRegisterFormData();
    clearOtpExpiry();
    await update({
      first_name: data.data.firstname,
      last_name: data.data.lastname,
      email: data.data.email,
      is_guest: "",
    });
  };

  const handleRegularRegistration = async (formData: any) => {
    const {
      email,
      password,
      firstname,
      lastname,
      phone: telephone,
      checkboxState,
      customer_group_id,
    } = formData;
    const offerz_plus_terms = formData.checkboxState.isTermsChecked;

    const result = await credentialSignIn({
      email,
      password,
      firstname,
      lastname,
      telephone,
      otp,
      offerz_plus_terms,
      customer_group_id,
      action: "register",
      newsletter: checkboxState.isNewsletterChecked ? 1 : 0,
      agree: checkboxState.isTermsChecked ? 1 : 0,
      lang,
    });

    if (!result || !result.ok) {
      showMessage(getAuthErrorMessage(result?.error), "warning");
      return;
    }
    if (isTooManyAttempts(result)) {
      showTooManyAttempts("error");
      return;
    }
    if (result?.error) {
      showMessage(getAuthErrorMessage(result.error), "warning");
      return;
    }

    clearOtpExpiry();
    if (checkoutOtp) {
      queryClient.invalidateQueries({ queryKey: ["payment-addresses"] });
      setActiveStep(2);
      return;
    }
    router.push("/register/success");
  };

  const handleRegister = async () => {
    if (submitInFlightRef.current) {
      return;
    }

    if (!requireOtp()) {
      return;
    }

    if (!startSubmit()) {
      return;
    }
    const savedFormData = getSavedRegisterFormData();

    if (!savedFormData) {
      showUnexpectedWarning();
      stopSubmit();
      return;
    }

    const formData = parseRegisterFormData(savedFormData);

    try {
      if (!isGuest) {
        await handleRegularRegistration(formData);
      } else {
        await handleLoggedInGuestRegistration(formData);
      }
    } catch (error) {
      if (isTooManyAttempts(error)) {
        showTooManyAttempts("error");
      } else {
        showMessage(t`An error occurred during registration`, "error");
      }
    } finally {
      stopSubmit();
    }
  };

  const handleResendOtp = async () => {
    setIsResendOtpLoaderVisible(true);

    if (isLoginFlow) {
      const savedLoginCredentials = getSavedLoginCredentials();
      if (!savedLoginCredentials) {
        setIsResendOtpLoaderVisible(false);
        return;
      }
      try {
        const credentials = await decryptCredentials(savedLoginCredentials);
        resendLoginOtpMutation.mutate({ email: credentials.email, password: credentials.password });
      } catch {
        setIsResendOtpLoaderVisible(false);
      }
      return;
    }

    const savedFormData = getSavedRegisterFormData();
    if (!savedFormData) {
      setIsResendOtpLoaderVisible(false);
      return;
    }
    const parsedFormData = parseRegisterFormData(savedFormData);
    const formData = new FormData();
    formData.append("email", parsedFormData.email);
    resendOtpMutation.mutate(formData);
  };

  useEffect(() => {
    const savedLoginCredentials = getSavedLoginCredentials();
    const savedFormData = getSavedRegisterFormData();

    // Set OTP expiry immediately so "minutes remaining" shows on first paint (don't wait for decrypt)
    const savedExpiry = getSavedOtpExpiry();
    if (savedExpiry && !savedFormData) {
      setCurrentOtpExpiry(savedExpiry);
    }

    const initLoginFlow = (emailValue: string) => {
      setIsLoginFlow(true);
      setEmail(emailValue);
      const expiry = getSavedOtpExpiry();
      if (expiry) {
        setCurrentOtpExpiry(expiry);
      }
      setResendOTP(true);
      setIsProgressDisplayed(false);
    };

    if (savedLoginCredentials) {
      decryptCredentials(savedLoginCredentials)
        .then((credentials) => {
          initLoginFlow(credentials.email);
        })
        .catch(() => {
          // Invalid or legacy format; try current-email as fallback
          const currentEmail = getSavedCurrentEmail();
          if (currentEmail) {
            initLoginFlow(currentEmail);
          }
        });
    } else if (savedFormData) {
      setIsLoginFlow(false);
      const parsedFormData = parseRegisterFormData(savedFormData);
      setEmail(parsedFormData.email);
      clearOtpExpiry();
      setCurrentOtpExpiry(undefined);
    } else {
      const currentEmail = getSavedCurrentEmail();
      if (currentEmail) {
        initLoginFlow(currentEmail);
      }
    }

    const timer = setInterval(() => {
      setProgress((prevProgress) => {
        if (prevProgress <= 0) {
          clearInterval(timer);
          setIsProgressDisplayed(false);
          return 0;
        }
        return prevProgress - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);
  useEffect(() => {
    if (isLoginFlow) {
      setResendOTP(true);
      setIsProgressDisplayed(false);
    } else {
      const savedEmail = getSavedCurrentEmail();
      setResendOTP(email !== savedEmail);
      setIsProgressDisplayed(email !== savedEmail);
    }
  }, [email, isLoginFlow]);

  /** Converts a naive datetime string (format: 'YYYY-MM-DD HH:mm:ss') in Europe/Zurich timezone to a UTC Date. */
  const swissTimeToUTC = useCallback((swissTimeStr: string): Date | null => {
    try {
      const [datePart, timePart] = swissTimeStr.trim().split(" ");
      const [year, month, day] = datePart.split("-").map(Number);
      const [hour, minute, second] = (timePart || "00:00:00").split(":").map(Number);

      const tempUTC = new Date(Date.UTC(year, month - 1, day, hour, minute, second || 0));

      const formatter = new Intl.DateTimeFormat("en-US", {
        timeZone: "Europe/Zurich",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      });

      const parts = formatter.formatToParts(tempUTC);
      const getPart = (type: string) => parseInt(parts.find((p) => p.type === type)?.value || "0");

      const desiredSwiss = new Date(Date.UTC(year, month - 1, day, hour, minute, second || 0));
      const actualSwiss = new Date(
        Date.UTC(
          getPart("year"),
          getPart("month") - 1,
          getPart("day"),
          getPart("hour"),
          getPart("minute"),
          getPart("second"),
        ),
      );

      // Offset between desired Swiss time and what Zurich actually shows for tempUTC
      const offsetMs = desiredSwiss.getTime() - actualSwiss.getTime();
      return new Date(tempUTC.getTime() + offsetMs);
    } catch {
      return null;
    }
  }, []);

  const getMinutesUntilExpiry = useCallback(
    (dateString: string): number | null => {
      try {
        const dateStr = dateString.trim().replace(" ", "T");
        const hasTimezone =
          dateStr.includes("Z") || /[+-]\d{2}:?\d{2}$/.test(dateStr) || /[+-]\d{4}$/.test(dateStr);

        const expiryDate = hasTimezone ? new Date(dateStr) : swissTimeToUTC(dateString);

        if (!expiryDate || isNaN(expiryDate.getTime())) return null;

        const diffMs = expiryDate.getTime() - Date.now();
        if (diffMs <= 0) {
          return null;
        }
        const diffMinutes = Math.ceil(diffMs / (1000 * 60));
        return diffMinutes;
      } catch {
        return null;
      }
    },
    [swissTimeToUTC],
  );

  useEffect(() => {
    if (!currentOtpExpiry) {
      setMinutesRemaining(null);
      clearOtpExpiry();
      return;
    }

    const updateMinutes = () => {
      const minutes = getMinutesUntilExpiry(currentOtpExpiry);
      setMinutesRemaining(minutes);
      if (minutes === null) {
        clearOtpExpiry();
      }
    };

    updateMinutes();
    const interval = setInterval(updateMinutes, 1000);
    return () => clearInterval(interval);
  }, [currentOtpExpiry, getMinutesUntilExpiry]);

  return (
    <div className="mx-auto w-full max-w-[450px] px-[15px] md:px-0">
      <Typography variant="h4" sx={{ mb: "15px" }}>
        <Trans>Verify email</Trans>
      </Typography>
      <Typography variant="body2" sx={{ mb: "40px" }}>
        {isLoginFlow ? (
          <Trans>
            We have sent you a 6-digit code via email. Please enter the code in the field below to
            verify your email.
          </Trans>
        ) : (
          <Trans>
            We have sent you a 4-digit code via email. Please enter the code in the field below to
            verify your email.
          </Trans>
        )}
      </Typography>
      {currentOtpExpiry && minutesRemaining !== null && (
        <Typography
          variant="body2"
          sx={{
            mb: "10px",
            color: "text.secondary",
            fontWeight: 500,
            fontSize: "14px",
          }}
        >
          <Trans>
            OTP expires in {minutesRemaining} {minutesRemaining === 1 ? "minute" : "minutes"}
          </Trans>
        </Typography>
      )}

      <Box sx={{ mb: "20px" }}>
        <TextField
          placeholder={t`Code`}
          required
          value={otp}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              if (submitInFlightRef.current) {
                return;
              }
              if (isLoginFlow) {
                handleLogin();
              } else {
                handleRegister();
              }
            }
          }}
          onInput={(e) => {
            const input = e.target as HTMLInputElement;
            input.value = input.value.replace(/\D/g, "");
          }}
          inputProps={{ maxLength: isLoginFlow ? 6 : 4 }}
          onChange={(e) => setOtp(e.target.value)}
          InputProps={{
            endAdornment: resendOTP && (
              <InputAdornment position="end" sx={{ pr: 0 }}>
                {isResendOtpLoaderVisible ? (
                  <CircularProgress size={20} sx={{ color: "primary.main" }} />
                ) : (
                  <Button
                    sx={{
                      fontWeight: 400,
                      fontSize: "14px",
                      "&.Mui-disabled": { color: "primary.main", opacity: 0.6 },
                    }}
                    disabled={isLoginFlow ? false : isProgressDisplayed}
                    variant="text"
                    disableRipple
                    onClick={handleResendOtp}
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    <Trans>Resend</Trans>
                  </Button>
                )}
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {!isLoginFlow && (
        <Box sx={{ mb: "20px" }}>
          <TextField
            value={email ?? ""}
            disabled
            sx={{
              "& .MuiOutlinedInput-root .Mui-disabled": {
                WebkitTextFillColor: "#000",
              },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" sx={{ pr: 0 }}>
                  <Button
                    variant="text"
                    sx={{ fontWeight: 400, fontSize: "14px" }}
                    disableRipple
                    onClick={handleChange}
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    <Trans>Change</Trans>
                  </Button>
                </InputAdornment>
              ),
            }}
          />
        </Box>
      )}

      {isProgressDisplayed && !isLoginFlow && (
        <Box sx={{ mb: "40px" }}>
          <Typography variant="h6" align="center" sx={{ mb: "10px" }}>
            <Trans>
              You may resend the<b className="px-[3px]">OTP</b>in
              <span className="px-[3px]">{progress}</span>
              seconds
            </Trans>
          </Typography>
          <LinearProgress
            variant="determinate"
            value={(OTP_COUNTDOWN_SECONDS - progress) * (100 / OTP_COUNTDOWN_SECONDS)}
            sx={{ mb: "20px" }}
          />
        </Box>
      )}

      <Button
        onClick={isLoginFlow ? handleLogin : handleRegister}
        color="primary"
        variant="contained"
        fullWidth
        disabled={isLoading}
        sx={{ mb: "50px", overflow: "hidden" }}
      >
        <Trans>Continue</Trans>
        <Typography component="span" className={`${isLoading ? "loading" : ""}`}></Typography>
      </Button>
    </div>
  );
};

export default OtpForm;
